import './DataQA.scss';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { useAppSelector } from '../../../redux';
import { useSetError } from '../../../redux/modals';
import * as UserRedux from '../../../redux/user';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import {
  EMPTY_DIFFS,
  INVARIANCE_CHECKED_SECTIONS,
  MEDIA_INPUTS_SECTIONS,
  REVIEW_UNIT_SECTIONS,
  REVIEW_UNITS,
  SectionKey,
  SectionKeyType,
  SectionStatus,
  SectionStatusType,
  SUMMARY_SECTIONS
} from './data-qa-constants';
import { dataQaReducer, initialDataQaState } from './data-qa-reducer';
import {
  ConfigSummary,
  MMMDataQACommentKind,
  MMMDataQAThreadScope,
  WorkspaceModelReview
} from './data-qa-types';
import {
  buildEmptyNotesRecord,
  buildEmptyStatusRecord,
  campaignPillStatus,
  groupConfigsByModel,
  modelLabelForConfig,
  resolveSelectedConfigPath
} from './data-qa-utils';
import { useMmmDataQaConfigs } from './hooks/useMmmDataQaConfigs';
import { useMmmDataQaLoad } from './hooks/useMmmDataQaLoad';
import { usePersistApproval } from './hooks/usePersistApproval';
import { usePrefetchOtherModels } from './hooks/usePrefetchOtherModels';
import { useModelLoadStates, useModelLoads } from './hooks/useModelLoads';
import { buildModelOutcomeRows } from './about-summary-utils';
import { useSectionDataInvariance } from './hooks/useSectionDataInvariance';
import { SessionModelReview, useSharedSectionReview } from './hooks/useSharedSectionReview';
import { ModelSwitcher } from './components/ModelSwitcher';
import { SectionStepper, STEPPER_STEPS, StepKey, StepKeyType } from './components/SectionStepper';
import { CommentsContext, CommentsContextValue, FlagRequest } from './components/CommentsContext';
import { CommentsDock } from './components/CommentsDock';
import {
  commentCountsByTab,
  mergeOpenThreads,
  ModelSectionHashes,
  openThreadsForScope,
  TabThreadOwner,
  tabThreadOwner
} from './comment-utils';
import { useCommentThreads, useResolvedThreads } from './hooks/useCommentThreads';
import { InlineAlert } from './components/InlineAlert';
import { ReviewNoticeContext } from './components/ReviewNoticeContext';
import { ReviewControlsSlotContext } from './components/ReviewControlsSlot';
import { buildReviewNotice } from './components/review-notice-utils';
import { LabeledNegativeFinding, NegativeValuesAlert } from './components/NegativeValuesAlert';
import { ApprovalToast } from './components/ApprovalToast';
import { FlagConfirmationModal } from './components/FlagConfirmationModal';
import { ContextVarsStage, ContextVarsStageTabs } from './components/ContextVarsStageTabs';
import { DependentVarStage, DependentVarStageTabs } from './components/DependentVarStageTabs';
import { AboutSection } from './sections/AboutSection';
import { CampaignsSection } from './sections/CampaignsSection';
import { ContextVarsSection } from './sections/ContextVarsSection';
import { DependentVarSection } from './sections/DependentVarSection';
import { FeatureMappingFocus, FeatureMappingSection } from './sections/FeatureMappingSection';
import { MediaInputsSection } from './sections/MediaInputsSection';
import { ReviewSummarySection } from './sections/ReviewSummarySection';
import {
  combineInherited,
  combineInvariance,
  combineSectionNotes,
  combineSectionStatus,
  reviewTitle
} from './media-inputs-utils';
import {
  findingsForSection,
  negativeValueBlockedSections,
  negativeValueColumnLabel,
  periodUnitFor
} from './negative-values-utils';

// The two sections that can stage-split for Brand Equity (see
// secondaryStageSectionDataInvariance below) -- hoisted to a stable module-level
// array rather than an inline literal at the call site, since useSectionDataInvariance
// takes this as a dependency of its own top-level useMemo: a fresh array identity on
// every render would defeat that memoization every render, not just when the actual
// section set changes.
// See DataQA's stepFocus.
interface StepFocus {
  channelKey?: string;
  feature?: FeatureMappingFocus;
}

const SECONDARY_STAGE_INVARIANCE_SECTIONS = [SectionKey.DEPENDENT_VAR, SectionKey.CONTEXT_VARS];

// The sections whose data decides whether a tab's comment thread is shared across models
// (see tabThreadOwner). About and Summary have none, so they're always the model's own;
// Campaigns is one thread per client anyway.
const THREAD_SHARING_SECTIONS: Partial<Record<StepKeyType, SectionKeyType[]>> = {
  [StepKey.MEDIA_INPUTS]: MEDIA_INPUTS_SECTIONS,
  [StepKey.FEATURE_MAPPING]: [SectionKey.FEATURE_MAPPING],
  [StepKey.CONTEXT_VARS]: [SectionKey.CONTEXT_VARS],
  [StepKey.DEPENDENT_VAR]: [SectionKey.DEPENDENT_VAR]
};

// One section, or Media Inputs' two (Portfolio + Channels) decided together.
type ReviewTarget = SectionKeyType | SectionKeyType[];
const targetSections = (target: ReviewTarget): SectionKeyType[] =>
  Array.isArray(target) ? target : [target];

const NO_CONFIGS: ConfigSummary[] = [];

export const DataQA = () => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  const { data: configs = NO_CONFIGS, error: configsError } = useMmmDataQaConfigs(cid);
  const [pickedConfigPath, setSelectedConfigPath] = useState<string | undefined>();
  // Derived, not reset in an effect, so a company switch never pairs the new cid with the old path.
  const selectedConfigPath = resolveSelectedConfigPath(configs, pickedConfigPath);
  const [state, dispatch] = useReducer(dataQaReducer, initialDataQaState);
  const [activeStep, setActiveStep] = useState<StepKeyType>(StepKey.ABOUT);
  // The step bar's slot the active tab renders its Approve/Flag controls into.
  const [reviewControlsSlot, setReviewControlsSlot] = useState<HTMLDivElement | null>(null);
  // Where to open the next step on, when it's opened from an About-tab row (a channel on
  // Media Inputs, a feature on Feature Mapping). Cleared by any plain step change.
  const [stepFocus, setStepFocus] = useState<StepFocus>({});
  const openStep = useCallback((step: StepKeyType, focus: StepFocus = {}) => {
    setStepFocus(focus);
    setActiveStep(step);
  }, []);

  const [toast, setToast] = useState<{
    show: boolean;
    title: string | null;
    status: SectionStatusType | null;
  }>({ show: false, title: null, status: null });

  // `target` distinguishes the primary model's flag (the common case) from
  // Brand Equity's secondary Dependent Variable/Context Variables stage,
  // which is tracked and submitted independently — see secondaryDvReview/
  // secondaryCvReview below.
  const [flagModal, setFlagModal] = useState<{
    show: boolean;
    sections: SectionKeyType[] | null;
    notes: string;
    target: 'primary' | 'secondaryDependentVar' | 'secondaryContextVars';
  }>({ show: false, sections: null, notes: '', target: 'primary' });

  // Keyed on the message: each failed refetch returns a new error object, which would
  // re-open the modal while the network is flaky.
  const configsErrorMessage = configsError?.message;
  useEffect(() => {
    if (!configsError) return;
    setError({ message: configsError.message, reportError: configsError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configsErrorMessage, setError]);

  // Cached per (cid, configPath) — switching back to an already-viewed model
  // resolves `loadResult` from cache on the same render, so the effect below
  // dispatches LOAD_SUCCESS immediately instead of re-running the backend's
  // Postgres/Redshift/CSV computation.
  const {
    data: loadResult,
    isError: isLoadError,
    error: loadError
  } = useMmmDataQaLoad(cid, selectedConfigPath);

  // Warms the cache for every other model once the active one has loaded, so
  // later switches are instant too — see usePrefetchOtherModels.
  usePrefetchOtherModels(cid, configs, selectedConfigPath, loadResult);

  useEffect(() => {
    if (!selectedConfigPath) return;
    if (loadResult) {
      dispatch({ type: 'LOAD_SUCCESS', result: loadResult });
    } else if (isLoadError) {
      dispatch({ type: 'LOAD_ERROR', message: loadError?.message ?? 'Failed to load model' });
    } else {
      dispatch({ type: 'LOAD_START' });
    }
  }, [selectedConfigPath, loadResult, isLoadError, loadError]);

  // Brand Equity's second stage config, loaded alongside the primary (already
  // warmed by usePrefetchOtherModels above) — every other product type's group
  // has just one config, so secondaryConfigPath is undefined and this hook is
  // simply disabled. Powers DependentVarStageTabs below; the reducer/`state`
  // above stays driven by the primary config only, unchanged.
  const activeGroup = useMemo(
    () =>
      groupConfigsByModel(configs).find(g =>
        g.configs.some(c => c.configPath === selectedConfigPath)
      ),
    [configs, selectedConfigPath]
  );
  const secondaryConfigPath = activeGroup?.configs[1]?.configPath;
  const secondaryLoad = useMmmDataQaLoad(cid, secondaryConfigPath);

  // The secondary stage's Dependent Variable review is tracked independently
  // of the primary's (a reviewer may decide differently on GQV vs. Total
  // Business) — seeded from its own persisted priorApproval, same as the
  // reducer does for the primary.
  // `inherited` mirrors the primary reducer's inheritedSections -- true only when the
  // auto-inherit effect below (not a genuine handleApprove/handleFlag call, and not
  // this stage's own real priorApproval) is what set the current status/notes. See
  // the mirror effect near sessionReviewByConfig, which reads this instead of
  // hardcoding `false`/`{}`.
  const [secondaryDvReview, setSecondaryDvReview] = useState<{
    status: SectionStatusType;
    notes: string;
    pending: boolean;
    inherited: boolean;
  }>({ status: SectionStatus.UNREVIEWED, notes: '', pending: false, inherited: false });

  useEffect(() => {
    const prior = secondaryLoad.data?.priorApproval;
    setSecondaryDvReview({
      status: prior?.sectionStatus[SectionKey.DEPENDENT_VAR] ?? SectionStatus.UNREVIEWED,
      notes: prior?.sectionNotes[SectionKey.DEPENDENT_VAR] ?? '',
      pending: false,
      inherited: false
    });
  }, [secondaryLoad.data]);

  // Same independent-tracking reasoning as secondaryDvReview above, for Context
  // Variables' own stage split — GQV and Total Business can genuinely declare
  // different context_vars (confirmed on real away data), so a reviewer may decide
  // differently on one stage than the other.
  const [secondaryCvReview, setSecondaryCvReview] = useState<{
    status: SectionStatusType;
    notes: string;
    pending: boolean;
    inherited: boolean;
  }>({ status: SectionStatus.UNREVIEWED, notes: '', pending: false, inherited: false });

  useEffect(() => {
    const prior = secondaryLoad.data?.priorApproval;
    setSecondaryCvReview({
      status: prior?.sectionStatus[SectionKey.CONTEXT_VARS] ?? SectionStatus.UNREVIEWED,
      notes: prior?.sectionNotes[SectionKey.CONTEXT_VARS] ?? '',
      pending: false,
      inherited: false
    });
  }, [secondaryLoad.data]);

  // Which stage tab (GQV/Total Business, etc.) is on screen for Dependent Variable and
  // Context Variables -- reported up via DependentVarStageTabs/ContextVarsStageTabs'
  // onActiveIndexChange, since the header's identical-data chip needs to know this (see
  // secondaryStageSectionDataInvariance above). Reset on model switch so a stale
  // "secondary tab was active" index from the previous model can't linger.
  const [activeDvStageIndex, setActiveDvStageIndex] = useState(0);
  const [activeCvStageIndex, setActiveCvStageIndex] = useState(0);
  useEffect(() => {
    setActiveDvStageIndex(0);
    setActiveCvStageIndex(0);
  }, [selectedConfigPath]);

  // --- Comments: the active tab's thread (see CommentsDock) ------------------------------
  // One thread per tab of a model: modelKey is the model's primary config path (it
  // survives data refreshes); Campaigns is one thread per client. Where a tab's data is
  // identical across models, they share one model's thread (see tabThreadOwner). Where
  // Dependent Variable or Context Variables split by Brand Equity stage, each stage has its
  // own thread.
  const userEmail = useAppSelector(UserRedux.emailSelector) ?? '';
  const isInternal = useAppSelector(UserRedux.isInternalSelector);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [flagRequest, setFlagRequest] = useState<FlagRequest | undefined>();
  const modelKey = activeGroup?.configs[0]?.configPath ?? '';
  // Each tab of each model remembers whether its chat is open, starting collapsed.
  const [openChats, setOpenChats] = useState<ReadonlySet<string>>(() => new Set());
  const chatKey = `${modelKey}|${activeStep}`;
  const chatOpen = openChats.has(chatKey);
  const setChatOpen = useCallback(
    (open: boolean) =>
      setOpenChats(prev => {
        const next = new Set(prev);
        if (open) {
          next.add(chatKey);
        } else {
          next.delete(chatKey);
        }
        return next;
      }),
    [chatKey]
  );
  const comments = useCommentThreads(cid ?? '', userEmail, chatOpen, isInternal);
  const postCommentMutate = comments.post.mutate;
  const modelLoadStates = useModelLoadStates(cid, configs, Boolean(state.dataset));
  const tabThreadOwners = useMemo((): Record<string, TabThreadOwner> => {
    const models = groupConfigsByModel(configs).map((group): ModelSectionHashes => ({
      modelKey: group.configs[0]?.configPath ?? '',
      label: group.label,
      stageHashes: group.configs.map(
        config => modelLoadStates.get(config.configPath)?.dataset.sectionContentHash
      )
    }));
    const openThreads = comments.threads.data ?? [];
    return Object.fromEntries(
      STEPPER_STEPS.map(({ key }) => [
        key,
        key === StepKey.CAMPAIGNS
          ? { modelKey: '', modelKeys: [''], sharedWithLabels: [] }
          : tabThreadOwner(models, modelKey, key, THREAD_SHARING_SECTIONS[key] ?? [], openThreads)
      ])
    );
  }, [configs, modelLoadStates, comments.threads.data, modelKey]);
  const activeThreadOwner = tabThreadOwners[activeStep];
  const activeThreadScope = useMemo((): MMMDataQAThreadScope => {
    if (activeStep === StepKey.CAMPAIGNS) return { modelKey: '', tab: activeStep, stage: '' };
    const splitSection =
      (activeStep === StepKey.DEPENDENT_VAR && SectionKey.DEPENDENT_VAR) ||
      (activeStep === StepKey.CONTEXT_VARS && SectionKey.CONTEXT_VARS) ||
      undefined;
    const primaryHash = splitSection && state.dataset?.sectionContentHash[splitSection];
    const secondaryHash =
      splitSection && secondaryLoad.data?.dataset.sectionContentHash[splitSection];
    const isSplit = Boolean(primaryHash && secondaryHash && primaryHash !== secondaryHash);
    const stageIndex =
      activeStep === StepKey.DEPENDENT_VAR ? activeDvStageIndex : activeCvStageIndex;
    if (isSplit) {
      return {
        modelKey,
        tab: activeStep,
        stage: activeGroup?.configs[stageIndex]?.configPath ?? ''
      };
    }
    return { modelKey: activeThreadOwner?.modelKey ?? modelKey, tab: activeStep, stage: '' };
  }, [
    activeStep,
    modelKey,
    activeThreadOwner,
    state.dataset,
    secondaryLoad.data,
    activeDvStageIndex,
    activeCvStageIndex,
    activeGroup
  ]);
  const threadLabel = useMemo(() => {
    const stepLabel = STEPPER_STEPS.find(step => step.key === activeStep)?.label ?? '';
    if (activeStep === StepKey.CAMPAIGNS) return `${stepLabel} · all models`;
    const modelLabel = state.config ? modelLabelForConfig(configs, state.config.configPath) : '';
    const stageConfigPath = activeThreadScope.stage;
    let stageLabel = '';
    if (stageConfigPath) {
      stageLabel =
        stageConfigPath === secondaryLoad.data?.config.configPath
          ? secondaryLoad.data.config.dependentVarLabel
          : (state.config?.dependentVarLabel ?? '');
    }
    return [stepLabel, modelLabel, stageLabel].filter(Boolean).join(' · ');
  }, [activeStep, state.config, configs, activeThreadScope.stage, secondaryLoad.data]);
  // A flag in progress belongs to the tab it was raised on: keyed on the active model, tab
  // and stage, so neither a data refresh nor the tab's sharing settling (which can move it
  // to another model's thread) drops a half-written flag.
  const activeThreadKey = `${modelKey}|${activeThreadScope.tab}|${activeThreadScope.stage}`;
  useEffect(() => {
    setFlagRequest(undefined);
    setHistoryOpen(false);
  }, [activeThreadKey]);
  // The active tab's resolved threads, while its history is open.
  const resolvedThreads = useResolvedThreads(
    cid ?? '',
    activeThreadScope,
    isInternal && chatOpen && historyOpen
  );
  // Every model sharing the tab's data (just this one where it isn't shared, or a stage is
  // split): the tab shows all their open threads as one, and its history covers all their
  // resolved ones, including any from before they started sharing.
  const activeModelKeys = useMemo(
    () => activeThreadOwner?.modelKeys ?? [activeThreadScope.modelKey],
    [activeThreadOwner, activeThreadScope.modelKey]
  );
  const activeOpenThreads = useMemo(
    () => openThreadsForScope(comments.threads.data ?? [], activeThreadScope, activeModelKeys),
    [comments.threads.data, activeThreadScope, activeModelKeys]
  );
  const activeThread = mergeOpenThreads(activeOpenThreads, activeThreadScope.modelKey);
  const historyThreads = resolvedThreads.data?.filter(t => activeModelKeys.includes(t.modelKey));
  const commentCounts = useMemo(
    () =>
      commentCountsByTab(
        comments.threads.data ?? [],
        Object.fromEntries(
          Object.entries(tabThreadOwners).map(([tab, owner]) => [tab, owner.modelKeys])
        )
      ),
    [comments.threads.data, tabThreadOwners]
  );
  const commentsContext = useMemo(
    (): CommentsContextValue => ({
      requestFlag: request => {
        setFlagRequest(request);
        setHistoryOpen(false);
        setChatOpen(true);
      }
    }),
    [setChatOpen]
  );

  // Session-wide (not just active-model) review tracking — the reducer only
  // ever holds the CURRENTLY active model's status/notes (LOAD_START wipes
  // them on every switch). Mirroring them here per configPath is what lets
  // useSharedSectionReview compare a section's review status across every
  // model still in this browsing session, not just the one on screen.
  const [sessionReviewByConfig, setSessionReviewByConfig] = useState<
    Record<string, SessionModelReview>
  >({});

  useEffect(() => {
    if (!selectedConfigPath || state.status !== 'ready') return;
    setSessionReviewByConfig(prev => ({
      ...prev,
      [selectedConfigPath]: {
        sectionStatus: state.sectionStatus,
        sectionNotes: state.sectionNotes,
        inheritedSections: state.inheritedSections
      }
    }));
  }, [
    selectedConfigPath,
    state.status,
    state.sectionStatus,
    state.sectionNotes,
    state.inheritedSections
  ]);

  // Mirrors a genuine decision made ON Brand Equity's secondary (Total Business)
  // stage into sessionReviewByConfig, keyed by its own configPath -- without this, such
  // a decision could never be inherited BY a later model (see the auto-inherit effect
  // below) even when that later model's data turns out to be identical to Total
  // Business's specifically, rather than GQV's (previously a known gap -- see the
  // secondaryStageSharedSectionReview comment below). Portfolio/Channels/Campaigns
  // aren't stage-split (DependentVarStageTabs/ContextVarsStageTabs only ever split
  // Dependent Variable/Context Variables) and are tracked only on the primary reducer,
  // so every other section here stays UNREVIEWED -- only the two genuinely stage-split
  // sections carry real status. inheritedSections reads secondaryDvReview.inherited/
  // secondaryCvReview.inherited (NOT hardcoded false) -- the secondary auto-inherit
  // effect below DOES call setSecondaryDvReview/setSecondaryCvReview with a status
  // copied from another model, so those two can themselves be inherited copies, not
  // always a genuine decision or this stage's own priorApproval. Without tracking that
  // here, an inherited copy could be mistaken for a valid attribution source by a THIRD
  // model's useSharedSectionReview -- the same contradictory-attribution bug
  // DataQaState.inheritedSections' own doc comment describes for the primary reducer.
  useEffect(() => {
    if (!secondaryConfigPath) return;
    setSessionReviewByConfig(prev => ({
      ...prev,
      [secondaryConfigPath]: {
        sectionStatus: {
          ...buildEmptyStatusRecord(),
          [SectionKey.DEPENDENT_VAR]: secondaryDvReview.status,
          [SectionKey.CONTEXT_VARS]: secondaryCvReview.status
        },
        sectionNotes: {
          ...buildEmptyNotesRecord(),
          [SectionKey.DEPENDENT_VAR]: secondaryDvReview.notes,
          [SectionKey.CONTEXT_VARS]: secondaryCvReview.notes
        },
        inheritedSections: {
          [SectionKey.DEPENDENT_VAR]: secondaryDvReview.inherited,
          [SectionKey.CONTEXT_VARS]: secondaryCvReview.inherited
        }
      }
    }));
  }, [
    secondaryConfigPath,
    secondaryDvReview.status,
    secondaryDvReview.notes,
    secondaryDvReview.inherited,
    secondaryCvReview.status,
    secondaryCvReview.notes,
    secondaryCvReview.inherited
  ]);

  const sharedSectionReview = useSharedSectionReview(
    cid,
    configs,
    selectedConfigPath,
    state.dataset?.sectionContentHash,
    sessionReviewByConfig,
    Boolean(state.dataset)
  );

  // Same secondary-stage blind spot as secondaryStageSectionDataInvariance below, for
  // the "Approved via …" header chip: anchored to Total Business's own
  // hash so a reviewer landing directly on that tab sees whether some OTHER model's
  // session review already covers it, not GQV's. sessionReviewByConfig is session-wide
  // already (keyed by whichever config was primary when each decision was made, AND now
  // by secondaryConfigPath too -- see the mirror effect above) -- this only changes
  // which hash the comparison is anchored to.
  const secondaryStageSharedSectionReview = useSharedSectionReview(
    cid,
    configs,
    secondaryConfigPath,
    secondaryLoad.data?.dataset.sectionContentHash,
    sessionReviewByConfig,
    Boolean(secondaryLoad.data)
  );

  // Proactive counterpart to sharedSectionReview above: tells the reviewer a
  // section's data is shared BEFORE anyone has reviewed anything, rather than only
  // after reviewing one model and switching to discover it was redundant.
  const sectionDataInvariance = useSectionDataInvariance(
    cid,
    configs,
    selectedConfigPath,
    state.dataset?.sectionContentHash,
    INVARIANCE_CHECKED_SECTIONS,
    Boolean(state.dataset)
  );

  // The banner above is anchored to the PRIMARY stage's own hash, so it's blind to
  // Brand Equity's SECONDARY stage (Total Business) even while that stage's own tab is
  // on screen (DependentVarStageTabs/ContextVarsStageTabs render both stages under one
  // top-level model selection). Without this second, secondary-anchored call, DTC's
  // chip could say "identical to Brand Equity (Total Business)" while Total
  // Business's own tab showed no reciprocal chip at all -- correct in isolation (each
  // side computes its own match independently) but reads as a bug, and Sean's ask is
  // exactly this: these messages must be bidirectional, X=Y implies Y=X, message it in
  // both places. Scoped to just the two sections that can stage-split -- every other
  // section only ever shows the primary stage, so the primary-anchored call above
  // already covers it. React Query dedupes the underlying fetches against the same
  // call above (identical queryKeys), so this adds no extra network requests.
  const secondaryStageSectionDataInvariance = useSectionDataInvariance(
    cid,
    configs,
    secondaryConfigPath,
    secondaryLoad.data?.dataset.sectionContentHash,
    SECONDARY_STAGE_INVARIANCE_SECTIONS,
    Boolean(secondaryLoad.data)
  );

  // See usePersistApproval's own doc comment for why this lives in its own hook
  // (concurrency-guarded writes, a cache patch to avoid a stale-on-reload bug) rather
  // than inline here. Declared before the auto-inherit effect below, because that
  // effect now calls it directly -- and a `const` referenced in an effect's
  // dependency array must already be initialized by the time that array is evaluated
  // during render (temporal dead zone).
  const persistApproval = usePersistApproval();

  // Sections that can't be approved because the data frame has negative values inside
  // the model window (see negative-values-utils). Brand Equity's second stage reads the
  // same data frame, so only its own dependent variable adds anything beyond the primary
  // stage's findings. Declared before the auto-inherit effects below, which read them.
  const blockedSections = useMemo(
    () => negativeValueBlockedSections(state.dataset),
    [state.dataset]
  );
  const secondaryDvBlockedReason = useMemo(
    () => negativeValueBlockedSections(secondaryLoad.data?.dataset).get(SectionKey.DEPENDENT_VAR),
    [secondaryLoad.data]
  );
  const stepperBlockedSections = useMemo(() => {
    const sections = new Set(blockedSections.keys());
    if (secondaryDvBlockedReason) {
      sections.add(SectionKey.DEPENDENT_VAR);
    }
    return sections;
  }, [blockedSections, secondaryDvBlockedReason]);

  // Auto-inherit a still-unreviewed section from another model that already
  // reviewed the identical underlying data this session (see
  // useSharedSectionReview) — this is the "don't make me review the same
  // Channels data twice" fix. Only fires while the section is genuinely
  // unreviewed for THIS model, so an explicit decision always sticks and is
  // never clobbered back to an inherited value. Carries over the OTHER
  // model's own notes verbatim (meaningful for a flagged section's reason) —
  // does NOT synthesize an explanatory string here, since the reducer clears
  // notes outright on an approved status (data-qa-reducer.ts) and
  // ReviewControls doesn't show notes once approved, so
  // anything stuffed into `notes` for that case would silently vanish. The
  // "why is this already reviewed" explanation is the header chip (see buildReviewNotice),
  // driven directly from sharedSectionReview rather than sectionNotes.
  // `inherited: true` marks the resulting status as a copy, not a genuine decision
  // -- useSharedSectionReview never treats an inherited status as a valid source for
  // a THIRD model to attribute to (see inheritedSections).
  //
  // Also persists a real approval record for THIS model via persistApproval, the
  // same finalizeApproval call handleApprove/handleFlag use -- an inherited status
  // used to be a session-only display copy, so Commerce/Brand Equity's own
  // ProgressMeter (driven by their own real priorApproval) kept showing "First
  // review" even after DTC's identical Portfolio was approved, and the stepper
  // badge reset on reload (Sean, 2026-08-27: "should reflect what's been reviewed
  // AND approved" / carry through across a reload, not just a session). Submits the
  // FULL current section map with every newly-inherited section applied at once
  // (same one-call, full-snapshot reasoning as handleApprove/handleFlag above --
  // finalizeApproval overwrites the whole snapshot per call), rather than one
  // network call per section. Errors surface via the shared error modal but never
  // block the dispatches above -- an inherited section still displays correctly
  // this session even if the background persist fails; it will simply retry next
  // time this effect's inputs change.
  useEffect(() => {
    if (!cid || !state.config || !state.dataset) return;
    const toInherit = SUMMARY_SECTIONS.flatMap(sectionKey => {
      const inherited = sharedSectionReview[sectionKey];
      if (!inherited || state.sectionStatus[sectionKey] !== SectionStatus.UNREVIEWED) return [];
      // A blocked section can't be approved directly, so it can't inherit an approval
      // either (a flag still carries over).
      if (inherited.status === SectionStatus.APPROVED && blockedSections.has(sectionKey)) {
        return [];
      }
      return [{ sectionKey, inherited }];
    });
    // Media Inputs is one decision across Portfolio and Channels: inherit one of them only
    // when the other is inherited alongside it, or already carries that same status.
    const toInheritTogether = toInherit.filter(({ sectionKey, inherited }) => {
      if (!MEDIA_INPUTS_SECTIONS.includes(sectionKey)) return true;
      return MEDIA_INPUTS_SECTIONS.filter(other => other !== sectionKey).every(
        other =>
          toInherit.some(t => t.sectionKey === other && t.inherited.status === inherited.status) ||
          state.sectionStatus[other] === inherited.status
      );
    });
    if (toInheritTogether.length === 0) return;

    const patchStatus: Partial<Record<SectionKeyType, SectionStatusType>> = {};
    const patchNotes: Partial<Record<SectionKeyType, string>> = {};
    toInheritTogether.forEach(({ sectionKey, inherited }) => {
      patchStatus[sectionKey] = inherited.status;
      patchNotes[sectionKey] = inherited.notes;
      dispatch({
        type: 'APPROVAL_SUCCESS',
        section: sectionKey,
        status: inherited.status,
        notes: inherited.notes,
        inherited: true
      });
    });

    const { configPath, configHash } = state.config;
    const { fingerprints } = state.dataset;
    persistApproval(
      cid,
      configPath,
      configHash,
      { sectionStatus: state.sectionStatus, sectionNotes: state.sectionNotes },
      { sectionStatus: patchStatus, sectionNotes: patchNotes },
      fingerprints
    ).catch(e => setError({ message: e.message, reportError: e }));
  }, [
    sharedSectionReview,
    blockedSections,
    state.sectionStatus,
    state.sectionNotes,
    cid,
    state.config,
    state.dataset,
    persistApproval,
    setError
  ]);

  // Secondary-stage counterpart to the auto-inherit effect above: lets Brand Equity's
  // Total Business stage inherit a still-unreviewed section from another model that
  // already reviewed the identical underlying data this session, the same way the
  // primary reducer already does -- previously only the reverse direction (mirroring a
  // genuine Total Business decision OUT to sessionReviewByConfig, see the mirror effect
  // near sessionReviewByConfig above) was built. Only DEPENDENT_VAR/CONTEXT_VARS apply
  // here -- the only two sections Total Business tracks independently (Portfolio/
  // Channels/Campaigns are shared with the primary reducer, not stage-split). Persists
  // via the same persistApproval handleApproveSecondaryDependentVar/
  // handleFlagSecondaryDependentVar already use, seeded from THIS config's own real
  // priorApproval (not the primary reducer's state, which belongs to a different
  // config).
  useEffect(() => {
    if (!cid || !secondaryLoad.data) return;

    const candidateDv =
      secondaryDvReview.status === SectionStatus.UNREVIEWED
        ? secondaryStageSharedSectionReview[SectionKey.DEPENDENT_VAR]
        : undefined;
    // Same blocked-section guard as the primary auto-inherit effect above.
    const inheritedDv =
      candidateDv?.status === SectionStatus.APPROVED && secondaryDvBlockedReason
        ? undefined
        : candidateDv;
    const inheritedCv =
      secondaryCvReview.status === SectionStatus.UNREVIEWED
        ? secondaryStageSharedSectionReview[SectionKey.CONTEXT_VARS]
        : undefined;
    if (!inheritedDv && !inheritedCv) return;

    const prior = secondaryLoad.data.priorApproval;
    const patchStatus: Partial<Record<SectionKeyType, SectionStatusType>> = {};
    const patchNotes: Partial<Record<SectionKeyType, string>> = {};

    if (inheritedDv) {
      patchStatus[SectionKey.DEPENDENT_VAR] = inheritedDv.status;
      patchNotes[SectionKey.DEPENDENT_VAR] = inheritedDv.notes;
      setSecondaryDvReview({
        status: inheritedDv.status,
        notes: inheritedDv.notes,
        pending: false,
        inherited: true
      });
    }
    if (inheritedCv) {
      patchStatus[SectionKey.CONTEXT_VARS] = inheritedCv.status;
      patchNotes[SectionKey.CONTEXT_VARS] = inheritedCv.notes;
      setSecondaryCvReview({
        status: inheritedCv.status,
        notes: inheritedCv.notes,
        pending: false,
        inherited: true
      });
    }

    persistApproval(
      cid,
      secondaryLoad.data.config.configPath,
      secondaryLoad.data.config.configHash,
      {
        sectionStatus: prior?.sectionStatus ?? buildEmptyStatusRecord(),
        sectionNotes: prior?.sectionNotes ?? buildEmptyNotesRecord()
      },
      { sectionStatus: patchStatus, sectionNotes: patchNotes },
      secondaryLoad.data.dataset.fingerprints
    ).catch(e => setError({ message: e.message, reportError: e }));
  }, [
    cid,
    secondaryLoad.data,
    secondaryStageSharedSectionReview,
    secondaryDvBlockedReason,
    secondaryDvReview.status,
    secondaryCvReview.status,
    persistApproval,
    setError
  ]);

  const handleNoteChange = useCallback((target: ReviewTarget, note: string) => {
    targetSections(target).forEach(section => dispatch({ type: 'SET_NOTE', section, note }));
  }, []);

  const handleApprove = useCallback(
    async (target: ReviewTarget) => {
      if (!cid || !state.config || !state.dataset) return;
      const sections = targetSections(target);
      sections.forEach(section => dispatch({ type: 'APPROVAL_PENDING', section, pending: true }));
      try {
        // persistApproval submits the FULL sectionStatus/sectionNotes map, not just
        // this one section -- finalizeApproval writes one fresh review snapshot per
        // call, and fetchPriorApproval only ever reads back the single most recent
        // one. `state.sectionStatus`/`sectionNotes` is only a fallback seed for
        // persistApproval's own running snapshot (see its doc comment) -- passing just
        // this section as the patch, rather than pre-merging it here, is what lets a
        // concurrent write (e.g. the auto-inherit effect firing around the same time)
        // merge on top of THIS approval instead of racing it.
        await persistApproval(
          cid,
          state.config.configPath,
          state.config.configHash,
          { sectionStatus: state.sectionStatus, sectionNotes: state.sectionNotes },
          {
            sectionStatus: Object.fromEntries(sections.map(s => [s, SectionStatus.APPROVED])),
            sectionNotes: Object.fromEntries(sections.map(s => [s, '']))
          },
          state.dataset.fingerprints
        );
        sections.forEach(section =>
          dispatch({ type: 'APPROVAL_SUCCESS', section, status: SectionStatus.APPROVED })
        );
        setToast({ show: true, title: reviewTitle(sections), status: SectionStatus.APPROVED });
      } catch (e) {
        sections.forEach(section =>
          dispatch({ type: 'APPROVAL_PENDING', section, pending: false })
        );
        setError({ message: e.message, reportError: e });
      }
    },
    [
      cid,
      persistApproval,
      setError,
      state.config,
      state.dataset,
      state.sectionNotes,
      state.sectionStatus
    ]
  );

  const handleFlag = useCallback(
    // Resolves to whether the flag was saved (see handleFlagConfirm).
    async (target: ReviewTarget, notes: string): Promise<boolean> => {
      if (!cid || !state.config || !state.dataset) return false;
      const sections = targetSections(target);
      sections.forEach(section => dispatch({ type: 'APPROVAL_PENDING', section, pending: true }));
      try {
        // Same patch/base reasoning as handleApprove above.
        await persistApproval(
          cid,
          state.config.configPath,
          state.config.configHash,
          { sectionStatus: state.sectionStatus, sectionNotes: state.sectionNotes },
          {
            sectionStatus: Object.fromEntries(sections.map(s => [s, SectionStatus.FLAGGED])),
            sectionNotes: Object.fromEntries(sections.map(s => [s, notes]))
          },
          state.dataset.fingerprints
        );
        sections.forEach(section =>
          dispatch({ type: 'APPROVAL_SUCCESS', section, status: SectionStatus.FLAGGED, notes })
        );
        setToast({ show: true, title: reviewTitle(sections), status: SectionStatus.FLAGGED });
        return true;
      } catch (e) {
        sections.forEach(section =>
          dispatch({ type: 'APPROVAL_PENDING', section, pending: false })
        );
        setError({ message: e.message, reportError: e });
        return false;
      }
    },
    [
      cid,
      persistApproval,
      setError,
      state.config,
      state.dataset,
      state.sectionNotes,
      state.sectionStatus
    ]
  );

  const handleFlagRequest = useCallback(
    (
      reviewTarget: ReviewTarget,
      notes: string,
      target: 'primary' | 'secondaryDependentVar' | 'secondaryContextVars' = 'primary'
    ) => {
      setFlagModal({ show: true, sections: targetSections(reviewTarget), notes, target });
    },
    []
  );

  // Approve/flag for Brand Equity's secondary Dependent Variable stage —
  // mirrors handleApprove/handleFlag above, but against secondaryLoad's own
  // config (so it submits with THAT config's own configHash) and the
  // independent secondaryDvReview state instead of the shared reducer.
  const handleApproveSecondaryDependentVar = useCallback(async () => {
    if (!cid || !secondaryLoad.data) return;
    setSecondaryDvReview(prev => ({ ...prev, pending: true }));
    try {
      const prior = secondaryLoad.data.priorApproval;
      await persistApproval(
        cid,
        secondaryLoad.data.config.configPath,
        secondaryLoad.data.config.configHash,
        {
          sectionStatus: prior?.sectionStatus ?? buildEmptyStatusRecord(),
          sectionNotes: prior?.sectionNotes ?? buildEmptyNotesRecord()
        },
        {
          sectionStatus: { [SectionKey.DEPENDENT_VAR]: SectionStatus.APPROVED },
          sectionNotes: { [SectionKey.DEPENDENT_VAR]: '' }
        },
        secondaryLoad.data.dataset.fingerprints
      );
      setSecondaryDvReview({
        status: SectionStatus.APPROVED,
        notes: '',
        pending: false,
        inherited: false
      });
    } catch (e) {
      setSecondaryDvReview(prev => ({ ...prev, pending: false }));
      setError({ message: e.message, reportError: e });
    }
  }, [cid, persistApproval, secondaryLoad.data, setError]);

  const handleFlagSecondaryDependentVar = useCallback(
    async (notes: string): Promise<boolean> => {
      if (!cid || !secondaryLoad.data) return false;
      setSecondaryDvReview(prev => ({ ...prev, pending: true }));
      try {
        const prior = secondaryLoad.data.priorApproval;
        await persistApproval(
          cid,
          secondaryLoad.data.config.configPath,
          secondaryLoad.data.config.configHash,
          {
            sectionStatus: prior?.sectionStatus ?? buildEmptyStatusRecord(),
            sectionNotes: prior?.sectionNotes ?? buildEmptyNotesRecord()
          },
          {
            sectionStatus: { [SectionKey.DEPENDENT_VAR]: SectionStatus.FLAGGED },
            sectionNotes: { [SectionKey.DEPENDENT_VAR]: notes }
          },
          secondaryLoad.data.dataset.fingerprints
        );
        setSecondaryDvReview({
          status: SectionStatus.FLAGGED,
          notes,
          pending: false,
          inherited: false
        });
        return true;
      } catch (e) {
        setSecondaryDvReview(prev => ({ ...prev, pending: false }));
        setError({ message: e.message, reportError: e });
        return false;
      }
    },
    [cid, persistApproval, secondaryLoad.data, setError]
  );

  const handleSecondaryDvNoteChange = useCallback((notes: string) => {
    setSecondaryDvReview(prev => ({ ...prev, notes }));
  }, []);

  // Approve/flag for Brand Equity's secondary Context Variables stage — mirrors
  // handleApproveSecondaryDependentVar/handleFlagSecondaryDependentVar above.
  const handleApproveSecondaryContextVars = useCallback(async () => {
    if (!cid || !secondaryLoad.data) return;
    setSecondaryCvReview(prev => ({ ...prev, pending: true }));
    try {
      const prior = secondaryLoad.data.priorApproval;
      await persistApproval(
        cid,
        secondaryLoad.data.config.configPath,
        secondaryLoad.data.config.configHash,
        {
          sectionStatus: prior?.sectionStatus ?? buildEmptyStatusRecord(),
          sectionNotes: prior?.sectionNotes ?? buildEmptyNotesRecord()
        },
        {
          sectionStatus: { [SectionKey.CONTEXT_VARS]: SectionStatus.APPROVED },
          sectionNotes: { [SectionKey.CONTEXT_VARS]: '' }
        },
        secondaryLoad.data.dataset.fingerprints
      );
      setSecondaryCvReview({
        status: SectionStatus.APPROVED,
        notes: '',
        pending: false,
        inherited: false
      });
    } catch (e) {
      setSecondaryCvReview(prev => ({ ...prev, pending: false }));
      setError({ message: e.message, reportError: e });
    }
  }, [cid, persistApproval, secondaryLoad.data, setError]);

  const handleFlagSecondaryContextVars = useCallback(
    async (notes: string): Promise<boolean> => {
      if (!cid || !secondaryLoad.data) return false;
      setSecondaryCvReview(prev => ({ ...prev, pending: true }));
      try {
        const prior = secondaryLoad.data.priorApproval;
        await persistApproval(
          cid,
          secondaryLoad.data.config.configPath,
          secondaryLoad.data.config.configHash,
          {
            sectionStatus: prior?.sectionStatus ?? buildEmptyStatusRecord(),
            sectionNotes: prior?.sectionNotes ?? buildEmptyNotesRecord()
          },
          {
            sectionStatus: { [SectionKey.CONTEXT_VARS]: SectionStatus.FLAGGED },
            sectionNotes: { [SectionKey.CONTEXT_VARS]: notes }
          },
          secondaryLoad.data.dataset.fingerprints
        );
        setSecondaryCvReview({
          status: SectionStatus.FLAGGED,
          notes,
          pending: false,
          inherited: false
        });
        return true;
      } catch (e) {
        setSecondaryCvReview(prev => ({ ...prev, pending: false }));
        setError({ message: e.message, reportError: e });
        return false;
      }
    },
    [cid, persistApproval, secondaryLoad.data, setError]
  );

  const handleSecondaryCvNoteChange = useCallback((notes: string) => {
    setSecondaryCvReview(prev => ({ ...prev, notes }));
  }, []);

  // Once the flag is saved, its reason goes into the thread it was raised from -- never
  // before, so a cancelled or failed flag leaves no comment behind.
  const handleFlagConfirm = useCallback(async () => {
    if (!flagModal.sections) return;
    setFlagModal(prev => ({ ...prev, show: false }));
    const scope = activeThreadScope;
    let saved: boolean;
    if (flagModal.target === 'secondaryDependentVar') {
      saved = await handleFlagSecondaryDependentVar(flagModal.notes);
    } else if (flagModal.target === 'secondaryContextVars') {
      saved = await handleFlagSecondaryContextVars(flagModal.notes);
    } else {
      saved = await handleFlag(flagModal.sections, flagModal.notes);
    }
    if (!saved) return;
    postCommentMutate(
      { ...scope, kind: MMMDataQACommentKind.FLAG, body: flagModal.notes },
      {
        onError: e =>
          setError({
            message: `Flagged, but the reason couldn't be added to the comments: ${e.message}`,
            reportError: e
          })
      }
    );
  }, [
    flagModal,
    handleFlag,
    handleFlagSecondaryDependentVar,
    handleFlagSecondaryContextVars,
    activeThreadScope,
    postCommentMutate,
    setError
  ]);

  // Multi-model workspace review for the Summary — one row per reviewable model
  // (Brand Equity's two stage configs collapse into one, matching the switcher;
  // see groupConfigsByModel). The active model uses its live session status (now
  // including Campaigns' own taxonomy-labeling pill, repeated here per Sean's
  // 2026-08-27 request so a "Label soon"/"Blocking" state doesn't only live on the
  // Campaigns tab itself); other models show as unreviewed/unchecked until the
  // backend provides their persisted per-model review (see WorkspaceModelReview) --
  // EXCEPT Campaigns specifically, which reuses sectionDataInvariance's already-
  // computed, group-aware match (Campaigns' own contentHash is byte-comparable
  // across models the same way Portfolio/Channels/etc. are -- see
  // INVARIANCE_CHECKED_SECTIONS) to repeat the active model's real pill onto every
  // OTHER model proven to share the identical underlying labeling data, per Sean's
  // 2026-08-27 request ("it applies universally... repeat this across eligible
  // models"). A model NOT in that matching list still shows nothing -- its own
  // Campaigns data hasn't been proven identical, so guessing would be wrong.
  const workspaceReview: WorkspaceModelReview[] = useMemo(() => {
    const activeCampaignStatus = campaignPillStatus(state.dataset?.campaignLabeling);
    const matchingCampaignModelLabels =
      sectionDataInvariance[SectionKey.CAMPAIGNS]?.matchingModelLabels ?? [];
    return groupConfigsByModel(configs).map(group => {
      const activeConfig = group.configs.find(c => c.configPath === selectedConfigPath);
      const isActive = Boolean(activeConfig);
      const primary = activeConfig ?? group.configs[0];
      const matchesActiveCampaignData = matchingCampaignModelLabels.some(
        label => label === group.label || label.startsWith(`${group.label} (`)
      );
      const campaignStatus =
        isActive || matchesActiveCampaignData ? activeCampaignStatus : undefined;
      return {
        configPath: primary.configPath,
        clientName: primary.clientName,
        productType: primary.productType,
        label: group.label,
        campaignStatus,
        negativeValuesBlocking: isActive ? stepperBlockedSections.size > 0 : undefined,
        sections: REVIEW_UNITS.map(key => ({
          key,
          status: isActive
            ? combineSectionStatus(REVIEW_UNIT_SECTIONS[key].map(s => state.sectionStatus[s]))
            : SectionStatus.UNREVIEWED,
          notes: isActive
            ? combineSectionNotes(REVIEW_UNIT_SECTIONS[key].map(s => state.sectionNotes[s]))
            : ''
        }))
      };
    });
  }, [
    configs,
    selectedConfigPath,
    state.sectionStatus,
    state.sectionNotes,
    state.dataset,
    sectionDataInvariance,
    stepperBlockedSections
  ]);

  // Brand Equity's second stage contributes only its own dependent-variable findings --
  // its media columns are the same data frame as the primary stage's.
  const negativeFindings: LabeledNegativeFinding[] = useMemo(() => {
    if (!state.config || !state.dataset) return [];
    const { featureMapping } = state.config;
    const primary = (state.dataset.negativeValues ?? []).map(finding => ({
      finding,
      label: negativeValueColumnLabel(finding.column, featureMapping)
    }));
    const secondary = secondaryLoad.data
      ? (secondaryLoad.data.dataset.negativeValues ?? [])
          .filter(finding => finding.kind === 'dependent')
          .map(finding => ({
            finding,
            label: `${finding.column} (${secondaryLoad.data?.config.dependentVarLabel} stage)`
          }))
      : [];
    return [...primary, ...secondary];
  }, [state.config, state.dataset, secondaryLoad.data]);

  // The persisted section(s) the active step reviews -- Media Inputs is Portfolio and
  // Channels together; About/Campaigns/Summary have no approve/flag status, so a
  // carried-over review doesn't apply to them.
  const activeSections: SectionKeyType[] =
    activeStep === StepKey.MEDIA_INPUTS
      ? MEDIA_INPUTS_SECTIONS
      : SUMMARY_SECTIONS.filter(section => section === activeStep);
  // Whichever stage tab is actually on screen for a stage-split section -- both
  // sharedSectionReview/sectionDataInvariance below are anchored to the PRIMARY
  // stage's own hash, so neither can tell Total Business's own matches from GQV's
  // without this. See secondaryStageSectionDataInvariance/
  // secondaryStageSharedSectionReview above.
  const isSecondaryStageActive =
    (activeStep === StepKey.DEPENDENT_VAR && activeDvStageIndex === 1) ||
    (activeStep === StepKey.CONTEXT_VARS && activeCvStageIndex === 1);
  const sharedReviewSource = isSecondaryStageActive
    ? secondaryStageSharedSectionReview
    : sharedSectionReview;
  const inheritedForActiveStep = combineInherited(
    activeSections.map(section => sharedReviewSource[section])
  );
  // Wider than activeSections above: Campaigns still benefits from the "identical
  // across models" signal even without approve/flag status, so it's included in
  // INVARIANCE_CHECKED_SECTIONS specifically (see data-qa-constants.ts).
  const activeInvarianceSections: SectionKeyType[] =
    activeStep === StepKey.MEDIA_INPUTS
      ? MEDIA_INPUTS_SECTIONS
      : INVARIANCE_CHECKED_SECTIONS.filter(section => section === activeStep);
  const invarianceSource = isSecondaryStageActive
    ? secondaryStageSectionDataInvariance
    : sectionDataInvariance;
  const dataInvarianceForActiveStep = combineInvariance(
    activeInvarianceSections.map(section => invarianceSource[section])
  );

  // The cross-model note (carried-over review, identical data, or still checking) rides
  // as a chip in the active section's header -- see SectionHeader. Campaigns has no
  // approve/flag, so its version only says not to re-check the table.
  const reviewNotice = buildReviewNotice(
    inheritedForActiveStep,
    dataInvarianceForActiveStep,
    activeSections.length > 0
  );

  // Every model's outcome KPI and review progress, for the About tab. Review progress is
  // this session's for the active model and any model reviewed this session, else the
  // model's last saved review.
  const modelLoads = useModelLoads(cid, configs, Boolean(state.dataset));
  const modelOutcomes = useMemo(
    () =>
      buildModelOutcomeRows(groupConfigsByModel(configs), selectedConfigPath, modelLoads, path =>
        path === selectedConfigPath
          ? state.sectionStatus
          : (sessionReviewByConfig[path]?.sectionStatus ??
            modelLoads.get(path)?.priorApproval?.sectionStatus)
      ),
    [configs, selectedConfigPath, modelLoads, state.sectionStatus, sessionReviewByConfig]
  );

  const renderStep = () => {
    if (!state.config || !state.dataset) return null;
    const sectionProps = (section: SectionKeyType) => ({
      diffs: state.diffsBySection[section] ?? EMPTY_DIFFS,
      status: state.sectionStatus[section],
      notes: state.sectionNotes[section],
      pending: state.pendingApproval[section],
      isColdStart: !state.priorApproval,
      onApprove: () => handleApprove(section),
      onFlag: (notes: string) => handleFlagRequest(section, notes),
      onNoteChange: (note: string) => handleNoteChange(section, note)
    });
    // One decision for Portfolio and Channels (see combineSectionStatus). Change tracking
    // uses Channels' channel-weeks, which cover any change Portfolio's totals would show.
    const mediaInputsProps = {
      diffs: state.diffsBySection[SectionKey.CHANNELS] ?? EMPTY_DIFFS,
      status: combineSectionStatus(MEDIA_INPUTS_SECTIONS.map(s => state.sectionStatus[s])),
      notes: combineSectionNotes(MEDIA_INPUTS_SECTIONS.map(s => state.sectionNotes[s])),
      pending: MEDIA_INPUTS_SECTIONS.some(s => state.pendingApproval[s]),
      isColdStart: !state.priorApproval,
      onApprove: () => handleApprove(MEDIA_INPUTS_SECTIONS),
      onFlag: (notes: string) => handleFlagRequest(MEDIA_INPUTS_SECTIONS, notes),
      onNoteChange: (note: string) => handleNoteChange(MEDIA_INPUTS_SECTIONS, note)
    };
    const mediaInputsBlockedReason =
      blockedSections.get(SectionKey.PORTFOLIO) ?? blockedSections.get(SectionKey.CHANNELS);
    switch (activeStep) {
      case StepKey.CAMPAIGNS:
        return (
          <CampaignsSection
            config={state.config}
            dataset={state.dataset}
            company={cid}
            configs={configs}
          />
        );
      case StepKey.FEATURE_MAPPING:
        return <FeatureMappingSection config={state.config} initialFocus={stepFocus.feature} />;
      case StepKey.CONTEXT_VARS: {
        // Same reasoning as StepKey.DEPENDENT_VAR below: Brand Equity's two stages
        // can genuinely declare different context_vars (confirmed on real away
        // data — GQV is missing three outlier flags Total Business has), so render
        // stage tabs only when their contextVars section hashes actually differ.
        const primaryHash = state.dataset.sectionContentHash[SectionKey.CONTEXT_VARS];
        const secondaryHash =
          secondaryLoad.data?.dataset.sectionContentHash[SectionKey.CONTEXT_VARS];
        if (secondaryLoad.data && primaryHash && secondaryHash && primaryHash !== secondaryHash) {
          const stages: ContextVarsStage[] = [
            {
              label: state.config.dependentVarLabel,
              config: state.config,
              dataset: state.dataset,
              ...sectionProps(SectionKey.CONTEXT_VARS)
            },
            {
              label: secondaryLoad.data.config.dependentVarLabel,
              config: secondaryLoad.data.config,
              dataset: secondaryLoad.data.dataset,
              diffs: secondaryLoad.data.diffsBySection[SectionKey.CONTEXT_VARS] ?? EMPTY_DIFFS,
              status: secondaryCvReview.status,
              notes: secondaryCvReview.notes,
              pending: secondaryCvReview.pending,
              isColdStart: !secondaryLoad.data.priorApproval,
              onApprove: handleApproveSecondaryContextVars,
              onNoteChange: handleSecondaryCvNoteChange,
              onFlag: (notes: string) =>
                handleFlagRequest(SectionKey.CONTEXT_VARS, notes, 'secondaryContextVars')
            }
          ];
          return (
            // Keyed on selectedConfigPath -- this component owns its own internal
            // activeIndex (reported up via onActiveIndexChange) and stays mounted
            // across a cached model switch (renderStep keeps returning it at the same
            // JSX position, no key change otherwise), so its internal index could
            // stay at 1 (secondary/Total Business) while DataQA.tsx's own
            // activeCvStageIndex resets to 0 in the effect above -- a parent/child
            // desync that fed isSecondaryStageActive the wrong stage. Forcing a
            // remount on switch keeps both in sync.
            <ContextVarsStageTabs
              key={selectedConfigPath}
              stages={stages}
              company={cid}
              onActiveIndexChange={setActiveCvStageIndex}
            />
          );
        }
        return (
          <ContextVarsSection
            config={state.config}
            dataset={state.dataset}
            {...sectionProps(SectionKey.CONTEXT_VARS)}
            company={cid}
          />
        );
      }
      case StepKey.DEPENDENT_VAR: {
        // Brand Equity's two stages always carry genuinely different target
        // metrics (GQV vs. Total Business) — render both as tabs, each with
        // its own independent approve/flag. Every other product type (and a
        // Brand Equity config whose secondary stage happens to produce the
        // same hash) falls through to the plain single-config view below.
        const primaryHash = state.dataset.sectionContentHash[SectionKey.DEPENDENT_VAR];
        const secondaryHash =
          secondaryLoad.data?.dataset.sectionContentHash[SectionKey.DEPENDENT_VAR];
        if (secondaryLoad.data && primaryHash && secondaryHash && primaryHash !== secondaryHash) {
          const stages: DependentVarStage[] = [
            {
              label: state.config.dependentVarLabel,
              config: state.config,
              dataset: state.dataset,
              ...sectionProps(SectionKey.DEPENDENT_VAR),
              approveBlockedReason: blockedSections.get(SectionKey.DEPENDENT_VAR)
            },
            {
              label: secondaryLoad.data.config.dependentVarLabel,
              config: secondaryLoad.data.config,
              dataset: secondaryLoad.data.dataset,
              diffs: secondaryLoad.data.diffsBySection[SectionKey.DEPENDENT_VAR] ?? EMPTY_DIFFS,
              status: secondaryDvReview.status,
              notes: secondaryDvReview.notes,
              pending: secondaryDvReview.pending,
              isColdStart: !secondaryLoad.data.priorApproval,
              onApprove: handleApproveSecondaryDependentVar,
              onNoteChange: handleSecondaryDvNoteChange,
              onFlag: (notes: string) =>
                handleFlagRequest(SectionKey.DEPENDENT_VAR, notes, 'secondaryDependentVar'),
              approveBlockedReason: secondaryDvBlockedReason
            }
          ];
          return (
            // Keyed on selectedConfigPath -- same parent/child activeIndex desync
            // risk as ContextVarsStageTabs above, forcing a remount on switch.
            <DependentVarStageTabs
              key={selectedConfigPath}
              stages={stages}
              onActiveIndexChange={setActiveDvStageIndex}
              company={cid}
              configs={configs}
            />
          );
        }
        return (
          <DependentVarSection
            config={state.config}
            dataset={state.dataset}
            company={cid}
            configs={configs}
            approveBlockedReason={blockedSections.get(SectionKey.DEPENDENT_VAR)}
            {...sectionProps(SectionKey.DEPENDENT_VAR)}
          />
        );
      }
      case StepKey.SUMMARY:
        return <ReviewSummarySection workspace={workspaceReview} />;
      case StepKey.ABOUT:
        return (
          <AboutSection
            config={state.config}
            dataset={state.dataset}
            secondaryPriorsStage={
              secondaryLoad.data
                ? {
                    label: secondaryLoad.data.config.dependentVarLabel,
                    priors: secondaryLoad.data.dataset.priors ?? []
                  }
                : undefined
            }
            models={modelOutcomes}
            onOpenStep={openStep}
            onOpenChannel={channelKey => openStep(StepKey.MEDIA_INPUTS, { channelKey })}
            onOpenFeature={feature => openStep(StepKey.FEATURE_MAPPING, { feature })}
            onSelectModel={setSelectedConfigPath}
          />
        );
      case StepKey.MEDIA_INPUTS:
      default:
        return (
          <MediaInputsSection
            config={state.config}
            dataset={state.dataset}
            company={cid}
            configs={configs}
            approveBlockedReason={mediaInputsBlockedReason}
            initialChannelKey={stepFocus.channelKey}
            {...mediaInputsProps}
          />
        );
    }
  };

  return (
    <Page pageType='MMM Data QA' title='MMM Data QA'>
      {/* One sticky unit so the active model stays visible under the step tabs no
          matter how far the user scrolls — previously each stuck to `top: 0`
          independently, so the switcher ended up hidden behind the stepper instead of
          below it. Also keeps the switcher above the status branches so a
          failed/loading model never hides it — the user can always pick another
          model. */}
      <div className='data-qa-sticky-header'>
        <SectionStepper
          activeStep={activeStep}
          sectionStatus={state.sectionStatus}
          onSelect={openStep}
          campaignStatus={campaignPillStatus(state.dataset?.campaignLabeling)}
          commentCounts={isInternal ? commentCounts : undefined}
          blockedSections={stepperBlockedSections}
        />
        <ModelSwitcher
          configs={configs}
          selectedConfigPath={selectedConfigPath}
          onSelect={setSelectedConfigPath}
          actions={<div ref={setReviewControlsSlot} />}
        />
      </div>
      <div className='mmm-data-qa'>
        {state.status === 'loading' && <FullPageSpinner />}
        {state.status === 'error' && (
          <InlineAlert variant='error' title='This model could not be loaded'>
            {state.errorMessage ?? 'Something went wrong loading this model.'}
            {configs.length > 1 ? ' Select another model above to continue.' : ''}
          </InlineAlert>
        )}
        {state.status === 'ready' && (
          <NegativeValuesAlert
            findings={activeSections.flatMap(section =>
              findingsForSection(negativeFindings, section)
            )}
            periodUnit={periodUnitFor(state.dataset?.reviewGranularity)}
            missingColumnCount={
              activeStep === StepKey.MEDIA_INPUTS
                ? (state.dataset?.negativeCheckMissingColumns?.length ?? 0)
                : 0
            }
          />
        )}
        {state.status === 'ready' && (
          <ReviewNoticeContext.Provider value={reviewNotice}>
            <ReviewControlsSlotContext.Provider value={reviewControlsSlot}>
              <CommentsContext.Provider value={isInternal ? commentsContext : null}>
                {renderStep()}
              </CommentsContext.Provider>
            </ReviewControlsSlotContext.Provider>
          </ReviewNoticeContext.Provider>
        )}
      </div>
      {/* Comments are for internal users only (the page itself is internal-only too). */}
      {state.status === 'ready' && isInternal && cid && modelKey && (
        <CommentsDock
          thread={activeThread}
          scopeLabel={threadLabel}
          sharedWith={activeThreadOwner?.sharedWithLabels ?? []}
          userEmail={userEmail}
          isLoading={comments.threads.isLoading}
          loadFailed={comments.threads.isError}
          open={chatOpen}
          onOpenChange={open => {
            setChatOpen(open);
            if (!open) {
              setFlagRequest(undefined);
              setHistoryOpen(false);
            }
          }}
          flagRequest={flagRequest}
          onFlagRequestDone={() => setFlagRequest(undefined)}
          onPost={body =>
            comments.post.mutateAsync({ ...activeThreadScope, body }).catch(e => {
              setError({ message: `Couldn't post your comment: ${e.message}`, reportError: e });
              // Keeps the draft in the composer.
              throw e;
            })
          }
          onResolve={note =>
            Promise.all(
              activeOpenThreads.map(t => comments.resolve.mutateAsync({ threadId: t.id, note }))
            )
              .then(() => true)
              .catch(e => {
                setError({ message: `Couldn't resolve the thread: ${e.message}`, reportError: e });
                return false;
              })
          }
          onDelete={commentId =>
            comments.remove
              .mutateAsync(commentId)
              .catch(e =>
                setError({ message: `Couldn't delete your comment: ${e.message}`, reportError: e })
              )
          }
          posting={comments.post.isPending}
          resolving={comments.resolve.isPending}
          historyOpen={historyOpen}
          onHistoryOpenChange={setHistoryOpen}
          resolvedThreads={historyThreads}
          historyLoading={resolvedThreads.isLoading}
          historyLoadFailed={resolvedThreads.isError}
        />
      )}
      <ApprovalToast
        show={toast.show}
        title={toast.title}
        status={toast.status}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />
      <FlagConfirmationModal
        show={flagModal.show}
        title={flagModal.sections ? reviewTitle(flagModal.sections) : null}
        onConfirm={handleFlagConfirm}
        onHide={() => setFlagModal(prev => ({ ...prev, show: false }))}
      />
    </Page>
  );
};
